import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';
import { useAuth } from '../../modules/auth';
import styleScss from './styles.module.scss';
import {
	getEventosEmpleabilidad,
	updateEventoEmpleabilidad,
	getLocalidades,
	getEstadosEventoEmpleabilidad,
	getTiposEventoEmpleabilidad,
	getTiposPoblacionPriorizadaEventoEmpleabilidad,
	getFranjasHorariasEventoEmpleabilidad,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { LoadingBackdrop } from '../../components/LoadingSpinner/LoadingSpinner';
import {
	Alert,
	AlertTitle,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	Menu,
	MenuItem,
	Paper,
	Select,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import PublishIcon from '@mui/icons-material/Publish';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import {
	Localidad,
	ParametroEventoEmpleabilidad,
	EventoEmpleabilidad,
	formatDateDisplay,
	getCupoTotalPrerregistro,
	buildPayload,
	eventoToForm,
	getErrorMessage,
} from './EventosEmpleabilidad.helpers';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';

// Total "prerregistrados / cupo total" y desglose por franja horaria (mismos datos que Ver/editar evento).
const PrerregistradosPorFranja = ({ evento }: { evento: EventoEmpleabilidad }) => {
	const franjas = evento.franjas ?? [];
	const cupoTotal = getCupoTotalPrerregistro(evento);
	const conteoTotal =
		franjas.length > 0
			? franjas.reduce((total, franja) => total + Number(franja.conteo_prerregistrados ?? 0), 0)
			: evento.conteo_prerregistrados ?? 0;

	return (
		<div style={{ whiteSpace: 'nowrap' }}>
			<strong>{`${conteoTotal} / ${cupoTotal ?? '-'}`}</strong>
			{franjas.map((franja) => (
				<div key={franja.franja_horaria_id} style={{ fontSize: '12.5px', color: '#6c757d' }}>
					{`${franja.Franja_horaria?.label || franja.Franja_horaria?.value || franja.franja_horaria_id}: ${
						franja.conteo_prerregistrados ?? 0
					} / ${franja.cupo_maximo_prerregistro}`}
				</div>
			))}
		</div>
	);
};

const EventosEmpleabilidadPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAdminRole, isCandidateRole, isEmployerRole, 
		isCounselorCandidateRole, isCounselorEmployerRole } = useAuth();
	const isAdmin = isAdminRole();
	const isCandidate = isCandidateRole();  
	const isCounselor = isCounselorCandidateRole() || isCounselorEmployerRole();
	const isEmployer = isEmployerRole();
	const getAreaPath = () => {
		if (isAdmin) return '/admin/area';
		if (isCandidateRole()) return '/candidato/area';
		if (isEmployerRole()) return '/empleador/area';
		return '/consejero/area';
	};
	const puedePrerregistrarse = (evento: EventoEmpleabilidad) => {
		return isCandidate && evento.requiere_prerregistro === 'SI' && getEstadoValue(evento) === 'PROGRAMADO';
	};	
	
	const estadoActivo = (evento: EventoEmpleabilidad) => {
		return getEstadoValue(evento) === 'ACTIVO';
	};

	const getEventosBasePath = () => {
		if (isAdmin) return '/admin/gestion-eventos';
		if (isCandidateRole()) return '/candidato/gestion-eventos';
		if (isEmployerRole()) return '/empleador/gestion-eventos';
		return '/consejero/gestion-eventos';
	};
	const [eventos, setEventos] = useState<EventoEmpleabilidad[]>([]);
	const [localidades, setLocalidades] = useState<Localidad[]>([]);
	const [estados, setEstados] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [tiposEvento, setTiposEvento] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [tiposPoblacionPriorizada, setTiposPoblacionPriorizada] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [franjasHorarias, setFranjasHorarias] = useState<ParametroEventoEmpleabilidad[]>([]);
	const [loading, setLoading] = useState(false);
	const [isSubmitting, setSubmitting] = useState(false);
	const [tipoFiltro, setTipoFiltro] = useState('');
	const [localidadFiltro, setLocalidadFiltro] = useState('');
	const [prerregistroFiltro, setPrerregistroFiltro] = useState('');
	const [estadoFiltro, setEstadoFiltro] = useState('');
	const [fechaDesde, setFechaDesde] = useState('');
	const [fechaHasta, setFechaHasta] = useState('');
	const [textoBuscar, setTextoBuscar] = useState('');
	const [filter, setFilter] = useState({
		page: 1,
		limit: 10,
		search: '',
		tipo_evento: '',
		localidad_id: '',
		requiere_prerregistro: '',
		estado: '',
		fecha_inicio: '',
		fecha_fin: '',
	});
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [open, setOpen] = useState(false);
	const [alert, setAlert] = useState(true);
	const [alertMsg, setAlertMsg] = useState('');

	const notify = (msg: string, success = true) => {
		setAlert(success);
		setAlertMsg(msg);
		setOpen(true);
		setTimeout(() => setOpen(false), 3000);
	};

	useEffect(() => {
		const incomingNotify = (location.state as any)?.notify;
		if (incomingNotify) {
			notify(incomingNotify.msg, incomingNotify.success);
			navigate(location.pathname, { replace: true, state: {} });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const localidadesById = useMemo(() => {
		const map: Record<number, string> = {};
		localidades.forEach((l) => {
			map[l.id_localidad] = l.desc_localidad;
		});
		return map;
	}, [localidades]);

	const estadosById = useMemo(() => {
		const map: Record<number, string> = {};
		estados.forEach((e) => {
			map[e.id] = e.label || e.value;
		});
		return map;
	}, [estados]);

	const ESTADOS_CONSULTA_PUBLICA = ['PROGRAMADO', 'ACTIVO'];
	const estadosFiltrables = useMemo(() => {
		if (isAdmin) return estados;
		return estados.filter((e) => ESTADOS_CONSULTA_PUBLICA.includes((e.value || '').toUpperCase()));
	}, [estados, isAdmin]);

	const tiposEventoByValue = useMemo(() => {
		const map: Record<string, string> = {};
		tiposEvento.forEach((t) => {
			map[t.value] = t.label || t.value;
			map[t.code] = t.label || t.value;
		});
		return map;
	}, [tiposEvento]);

	const tiposPoblacionPriorizadaByValue = useMemo(() => {
		const map: Record<string, string> = {};
		tiposPoblacionPriorizada.forEach((t) => {
			map[t.value] = t.label || t.value;
			map[t.code] = t.label || t.value;
		});
		return map;
	}, [tiposPoblacionPriorizada]);

	const fetchEventos = async () => {
		try {
			setLoading(true);
			const params: any = { page: filter.page, limit: filter.limit };
			if (filter.search) params.search = filter.search;
			if (filter.tipo_evento) params.tipo_evento = filter.tipo_evento;
			if (filter.localidad_id) params.localidad_id = filter.localidad_id;
			if (filter.requiere_prerregistro) params.requiere_prerregistro = filter.requiere_prerregistro;
			if (filter.estado) params.estado = filter.estado;
			if (filter.fecha_inicio) params.fecha_inicio = filter.fecha_inicio;
			if (filter.fecha_fin) params.fecha_fin = filter.fecha_fin;

			const response = await getEventosEmpleabilidad(params);
			if (response && response.data) {
				setEventos(response.data.data || []);
				setTotal(response.data.total || 0);
			}
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setLoading(false);
		}
	};

	const estaEnFechaDeEvento = (evento: EventoEmpleabilidad) => {
		const ahora = new Date();
		const hoy = `${ahora.getFullYear()}-${String(ahora.getMonth() + 1).padStart(2, '0')}-${String(ahora.getDate()).padStart(2, '0')}`;
		const fechaInicio = String(evento.fecha_inicio_evento).slice(0, 10);
		const fechaFin = String(evento.fecha_fin_evento).slice(0, 10);

		return hoy >= fechaInicio && hoy <= fechaFin;
	};

	useEffect(() => {
		fetchEventos();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	useEffect(() => {
		getLocalidades()
			.then((response: any) => {
				if (response && response.data) setLocalidades(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getEstadosEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setEstados(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getTiposEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setTiposEvento(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getTiposPoblacionPriorizadaEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setTiposPoblacionPriorizada(response.data);
			})
			.catch(() => {});
	}, []);

	useEffect(() => {
		getFranjasHorariasEventoEmpleabilidad()
			.then((response: any) => {
				if (response && response.data) setFranjasHorarias(response.data);
			})
			.catch(() => {});
	}, []);

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		setFilter({ ...filter, page: newPage + 1 });
	};

	const handleChangeRowsPerPage = (event: any) => {
		const newLimit = parseInt(event.target.value, 10);
		setRowsPerPage(newLimit);
		setPage(0);
		setFilter({ ...filter, page: 1, limit: newLimit });
	};

	const handleFirstPageButtonClick = () => handleChangePage(0);
	const handleLastPageButtonClick = () => handleChangePage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));

	const filtrarTextoBuscar = () => {
		setPage(0);
		setFilter({ ...filter, page: 1, search: textoBuscar });
	};

	const handleKeyDownEnterTextoBuscar = (event: any) => {
		if (event.key === 'Enter') filtrarTextoBuscar();
	};

	const aplicarFiltroFecha = (desde: string, hasta: string) => {
		setPage(0);
		setFilter({ ...filter, page: 1, fecha_inicio: desde, fecha_fin: hasta });
	};

	const hayFiltrosActivos = Boolean(
		textoBuscar || tipoFiltro || localidadFiltro || prerregistroFiltro || estadoFiltro || fechaDesde || fechaHasta
	);

	const handleLimpiarFiltros = () => {
		setTextoBuscar('');
		setTipoFiltro('');
		setLocalidadFiltro('');
		setPrerregistroFiltro('');
		setEstadoFiltro('');
		setFechaDesde('');
		setFechaHasta('');
		setPage(0);
		setFilter({
			page: 1,
			limit: filter.limit,
			search: '',
			tipo_evento: '',
			localidad_id: '',
			requiere_prerregistro: '',
			estado: '',
			fecha_inicio: '',
			fecha_fin: '',
		});
	};

	// Cancelar evento (cancelación lógica: cambio de estado a "Cancelado", no se elimina el registro)
	const [openCancelar, setOpenCancelar] = useState(false);
	const [eventoCancelar, setEventoCancelar] = useState<EventoEmpleabilidad | null>(null);
	const handleOpenCancelar = (evento: EventoEmpleabilidad) => {
		setEventoCancelar(evento);
		setOpenCancelar(true);
	};
	const handleConfirmarCancelar = async () => {
		if (!eventoCancelar) return;
		try {
			setSubmitting(true);
			const estadoCancelado = estados.find((e) => (e.value || e.label || '').toUpperCase() === 'CANCELADO');
			if (!estadoCancelado) {
				notify('No se encontró el estado "Cancelado" configurado en los parámetros.', false);
				return;
			}
			const formCancelado = { ...eventoToForm(eventoCancelar), estado: String(estadoCancelado.id) };
			const payload = buildPayload(formCancelado, { isCreate: false });
			await updateEventoEmpleabilidad(eventoCancelar.id, payload);
			notify('Evento cancelado con éxito.');
			setOpenCancelar(false);
			fetchEventos();
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setSubmitting(false);
		}
	};

	// Activar evento (cambio de estado a "Activo")
	const [openActivar, setOpenActivar] = useState(false);
	const [eventoActivar, setEventoActivar] = useState<EventoEmpleabilidad | null>(null);
	const handleOpenActivar = (evento: EventoEmpleabilidad) => {
		setEventoActivar(evento);
		setOpenActivar(true);
	};
	const handleConfirmarActivar = async () => {
		if (!eventoActivar) return;
		try {
			setSubmitting(true);
			const estadoActivo = estados.find((e) => (e.value || e.label || '').toUpperCase() === 'ACTIVO');
			if (!estadoActivo) {
				notify('No se encontró el estado "Activo" configurado en los parámetros.', false);
				return;
			}
			const formActivado = { ...eventoToForm(eventoActivar), estado: String(estadoActivo.id) };
			const payload = buildPayload(formActivado, { isCreate: false });
			await updateEventoEmpleabilidad(eventoActivar.id, payload);
			notify('Evento activado con éxito.');
			setOpenActivar(false);
			fetchEventos();
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setSubmitting(false);
		}
	};

	// Reactivar evento (cambio de estado de "Cancelado" a "Creado")
	const [openReactivar, setOpenReactivar] = useState(false);
	const [eventoReactivar, setEventoReactivar] = useState<EventoEmpleabilidad | null>(null);
	const handleOpenReactivar = (evento: EventoEmpleabilidad) => {
		setEventoReactivar(evento);
		setOpenReactivar(true);
	};
	const handleConfirmarReactivar = async () => {
		if (!eventoReactivar) return;
		try {
			setSubmitting(true);
			const estadoCreado = estados.find((e) => (e.value || e.label || '').toUpperCase() === 'CREADO');
			if (!estadoCreado) {
				notify('No se encontró el estado "Creado" configurado en los parámetros.', false);
				return;
			}
			const formReactivado = { ...eventoToForm(eventoReactivar), estado: String(estadoCreado.id) };
			const payload = buildPayload(formReactivado, { isCreate: false });
			await updateEventoEmpleabilidad(eventoReactivar.id, payload);
			notify('Evento reactivado con éxito.');
			setOpenReactivar(false);
			fetchEventos();
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setSubmitting(false);
		}
	};

	// Publicar evento (cambio de estado a "Programado")
	const [openPublicar, setOpenPublicar] = useState(false);
	const [eventoPublicar, setEventoPublicar] = useState<EventoEmpleabilidad | null>(null);
	const handleOpenPublicar = (evento: EventoEmpleabilidad) => {
		setEventoPublicar(evento);
		setOpenPublicar(true);
	};
	const handleConfirmarPublicar = async () => {
		if (!eventoPublicar) return;
		try {
			setSubmitting(true);
			const estadoProgramado = estados.find((e) => (e.value || e.label || '').toUpperCase() === 'PROGRAMADO');
			if (!estadoProgramado) {
				notify('No se encontró el estado "Programado" configurado en los parámetros.', false);
				return;
			}
			const formPublicado = { ...eventoToForm(eventoPublicar), estado: String(estadoProgramado.id) };
			const payload = buildPayload(formPublicado, { isCreate: false });
			await updateEventoEmpleabilidad(eventoPublicar.id, payload);
			notify('Evento publicado con éxito.');
			setOpenPublicar(false);
			fetchEventos();
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setSubmitting(false);
		}
	};

	// Finalizar evento (cambio de estado de "Activo" a "Cerrado")
	const [openFinalizar, setOpenFinalizar] = useState(false);
	const [eventoFinalizar, setEventoFinalizar] = useState<EventoEmpleabilidad | null>(null);
	const handleOpenFinalizar = (evento: EventoEmpleabilidad) => {
		setEventoFinalizar(evento);
		setOpenFinalizar(true);
	};
	const handleConfirmarFinalizar = async () => {
		if (!eventoFinalizar) return;
		try {
			setSubmitting(true);
			const estadoFinalizado = estados.find((e) => (e.value || e.label || '').toUpperCase() === 'FINALIZADO');
			if (!estadoFinalizado) {
				notify('No se encontró el estado "Finalizado" configurado en los parámetros.', false);
				return;
			}
			const formFinalizado = { ...eventoToForm(eventoFinalizar), estado: String(estadoFinalizado.id) };
			const payload = buildPayload(formFinalizado, { isCreate: false });
			await updateEventoEmpleabilidad(eventoFinalizar.id, payload);
			notify('Evento finalizado con éxito.');
			setOpenFinalizar(false);
			fetchEventos();
		} catch (error: any) {
			notify(getErrorMessage(error), false);
		} finally {
			setSubmitting(false);
		}
	};

	const getLocalidadLabel = (evento: EventoEmpleabilidad): string => {
		if (evento.Localidad?.desc_localidad) return evento.Localidad.desc_localidad;
		return localidadesById[evento.localidad_id] || 'N/A';
	};

	const getEstadoLabel = (evento: EventoEmpleabilidad): string => {
		if (evento.Estado?.label) return evento.Estado.label;
		if (evento.Estado?.value) return evento.Estado.value;
		return (evento.estado && estadosById[evento.estado]) || 'N/A';
	};

	const getTipoEventoLabel = (evento: EventoEmpleabilidad): string => {
		return tiposEventoByValue[evento.tipo_evento] || evento.tipo_evento;
	};

	const getEstadoValue = (evento: EventoEmpleabilidad): string => {
		if (evento.Estado?.value) return evento.Estado.value.toUpperCase();
		const estado = estados.find((e) => e.id === evento.estado);
		return (estado?.value || '').toUpperCase();
	};

	// Reglas de transición: la acción solo se muestra si el evento está en uno de estos estados.
	const puedeEditar = (evento: EventoEmpleabilidad) => ['CREADO', 'PROGRAMADO', 'ACTIVO'].includes(getEstadoValue(evento));
	const puedePublicar = (evento: EventoEmpleabilidad) => getEstadoValue(evento) === 'CREADO';
	const puedeActivar = (evento: EventoEmpleabilidad) => getEstadoValue(evento) === 'PROGRAMADO';
	const puedeCancelar = (evento: EventoEmpleabilidad) => ['CREADO', 'PROGRAMADO', 'ACTIVO'].includes(getEstadoValue(evento));
	const puedeReactivar = (evento: EventoEmpleabilidad) => getEstadoValue(evento) === 'CANCELADO';
	const puedeFinalizar = (evento: EventoEmpleabilidad) => getEstadoValue(evento) === 'ACTIVO';
	const requierePrerregistro = (evento: EventoEmpleabilidad) => evento.requiere_prerregistro === 'SI';
	const isEventoEnNoCreado = (evento: EventoEmpleabilidad) => ['PROGRAMADO', 'ACTIVO', 'CANCELADO', 'CERRADO'].includes(getEstadoValue(evento));
	const isEventoCreadoOProgramado = (evento: EventoEmpleabilidad) => ['CREADO', 'PROGRAMADO'].includes(getEstadoValue(evento));

	const MenuAction = ({ evento }: { evento: EventoEmpleabilidad }) => {
		const [anchorEl, setAnchorEl] = useState(null);
		const handleClick = (e: any) => setAnchorEl(e.currentTarget);
		const handleClose = () => setAnchorEl(null);
		const basePath = getEventosBasePath();

		return (
			<>
				<Button onClick={handleClick}>
					<i className='bi bi-three-dots' style={{ fontSize: '20px' }}></i>
				</Button>
				<Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
					{isAdmin && (
						<MenuItem
							onClick={() => {
								handleClose();
								navigate(`${basePath}/ver/${evento.id}`);
							}}
						>
							<VisibilityIcon />
							&nbsp; Ver evento
						</MenuItem>
					)}
					{(isCandidate || isCounselor || isEmployer) && (
						<MenuItem
							onClick={() => {
								handleClose();
								navigate(`${basePath}/informacion/${evento.id}`, { state: { readOnly: true } });
							}}
						>
							<InfoOutlinedIcon />
							&nbsp; Visualización información evento
						</MenuItem>
					)}

					{(puedePrerregistrarse(evento)) && (
						<MenuItem
							onClick={() => {
								handleClose();
								navigate(`${basePath}/informacion/${evento.id}`, { state: { readOnly: false } });
							}}
						>
							< AppRegistrationIcon/>
							&nbsp; Prerregistrar candidato evento
						</MenuItem>
					)}

					{ (isAdminRole() || isCounselor) && estaEnFechaDeEvento(evento) && estadoActivo(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								const targetUrl = `${basePath}/eventos-empleabilidad/${evento.id}/asistencia/buscar-candidato`;
								navigate(targetUrl, {state: { evento } });
							}}
						>
							<PersonAddAltIcon/>
							&nbsp; Registro asistencia evento
						</MenuItem>
					)}

				{isAdminRole() && !isEventoCreadoOProgramado(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								const targetUrl = `${basePath}/eventos-empleabilidad/${evento.id}/asistencia/estadisticas`;
								navigate(targetUrl);
							}}
						>
							<HowToRegIcon/>
							&nbsp; Ver candidatos asistentes a eventos
						</MenuItem>
					)}

					{(isAdmin && isEventoEnNoCreado(evento) && requierePrerregistro(evento)) && (
					<MenuItem
						onClick={() => {
						handleClose();
						const targetUrl = `${basePath}/eventos-empleabilidad/${evento.id}/candidatos-prerregistrados`;
								navigate(targetUrl);
						}}
					>
						<GroupAddIcon/>
						&nbsp; Ver candidatos prerregistrados evento
					</MenuItem>
					)}
										
					{isAdmin && puedeEditar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								navigate(`${basePath}/editar/${evento.id}`);
							}}
						>
							<BorderColorIcon />
							&nbsp; Editar
						</MenuItem>
					)}
					{isAdmin && puedePublicar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								handleOpenPublicar(evento);
							}}
						>
							<PublishIcon />
							&nbsp; Publicar evento
						</MenuItem>
					)}
					{isAdmin && puedeActivar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								handleOpenActivar(evento);
							}}
						>
							<CheckCircleIcon />
							&nbsp; Activar evento
						</MenuItem>
					)}
					{isAdmin && puedeFinalizar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								handleOpenFinalizar(evento);
							}}
						>
							<DoneAllIcon />
							&nbsp; Finalizar evento
						</MenuItem>
					)}
					{isAdmin && puedeCancelar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								handleOpenCancelar(evento);
							}}
						>
							<CancelIcon />
							&nbsp; Cancelar evento
						</MenuItem>
					)}
					{isAdmin && puedeReactivar(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								handleOpenReactivar(evento);
							}}
						>
							<RestoreIcon />
							&nbsp; Reactivar evento
						</MenuItem>
					)}
					
					{(isAdmin || isEmployer || isCounselor) && estadoActivo(evento) && (
						<MenuItem
							onClick={() => {
								handleClose();
								navigate(`${basePath}/marcacion-remision/${evento.id}`);
							}}
						>
							<SchoolOutlinedIcon />
							 &nbsp; Marcación de Remisión
						</MenuItem>
					)}
				</Menu>
			</>
		);
	};

	return (
		<>
			<div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
				<LoadingBackdrop loading={isSubmitting} />
				<div className='row mb-4'>
					<div className='col-sm-12'>
						<div className='d-flex flex-wrap align-items-center mb-3' style={{ gap: '10px' }}>
							<BackButton to={getAreaPath()} />
						</div>
						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(4, 1fr)',
								gap: '12px',
								alignItems: 'end',
							}}
						>
							<TextField
								fullWidth
								size='small'
								value={textoBuscar}
								onChange={(e) => setTextoBuscar(e.target.value)}
								onKeyDown={handleKeyDownEnterTextoBuscar}
								label='Código / Nombre del evento'
								variant='outlined'
								InputProps={{
									endAdornment: (
										<IconButton onClick={filtrarTextoBuscar}>
											<i className={`bi ${styleScss['bi-search2']} bi-search`} style={{ fontSize: '20px' }}></i>
										</IconButton>
									),
								}}
							/>
							<FormControl fullWidth size='small'>
								<InputLabel id='tipo-evento-label'>Tipo de evento</InputLabel>
								<Select
									labelId='tipo-evento-label'
									label='Tipo de evento'
									value={tipoFiltro}
									onChange={(e) => {
										setTipoFiltro(e.target.value);
										setPage(0);
										setFilter({ ...filter, page: 1, tipo_evento: e.target.value });
									}}
								>
									<MenuItem value=''>Todos</MenuItem>
									{tiposEvento.map((tipo) => (
										<MenuItem key={tipo.id} value={tipo.value}>
											{tipo.label || tipo.value}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<TextField
								fullWidth
								size='small'
								type='date'
								label='Fecha inicio evento (desde)'
								InputLabelProps={{ shrink: true }}
								value={fechaDesde}
								onChange={(e) => {
									setFechaDesde(e.target.value);
									aplicarFiltroFecha(e.target.value, fechaHasta);
								}}
							/>
							<TextField
								fullWidth
								size='small'
								type='date'
								label='Fecha inicio evento (hasta)'
								InputLabelProps={{ shrink: true }}
								value={fechaHasta}
								onChange={(e) => {
									setFechaHasta(e.target.value);
									aplicarFiltroFecha(fechaDesde, e.target.value);
								}}
							/>
							<FormControl fullWidth size='small'>
								<InputLabel id='estado-filtro-label'>Estado</InputLabel>
								<Select
									labelId='estado-filtro-label'
									label='Estado'
									value={estadoFiltro}
									onChange={(e) => {
										setEstadoFiltro(e.target.value);
										setPage(0);
										setFilter({ ...filter, page: 1, estado: e.target.value });
									}}
								>
									<MenuItem value=''>Todos</MenuItem>
									{estadosFiltrables.map((estado) => (
										<MenuItem key={estado.id} value={String(estado.id)}>
											{estado.label || estado.value}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size='small'>
								<InputLabel id='localidad-filtro-label'>Localidad</InputLabel>
								<Select
									labelId='localidad-filtro-label'
									label='Localidad'
									value={localidadFiltro}
									onChange={(e) => {
										setLocalidadFiltro(e.target.value);
										setPage(0);
										setFilter({ ...filter, page: 1, localidad_id: e.target.value });
									}}
								>
									<MenuItem value=''>Todas</MenuItem>
									{localidades.map((localidad) => (
										<MenuItem key={localidad.id_localidad} value={String(localidad.id_localidad)}>
											{localidad.desc_localidad}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl fullWidth size='small'>
								<InputLabel id='prerregistro-filtro-label'>¿Prerregistro?</InputLabel>
								<Select
									labelId='prerregistro-filtro-label'
									label='¿Prerregistro?'
									value={prerregistroFiltro}
									onChange={(e) => {
										setPrerregistroFiltro(e.target.value);
										setPage(0);
										setFilter({ ...filter, page: 1, requiere_prerregistro: e.target.value });
									}}
								>
									<MenuItem value=''>Todos</MenuItem>
									<MenuItem value='SI'>Sí</MenuItem>
									<MenuItem value='NO'>No</MenuItem>
								</Select>
							</FormControl>
							<div style={{ display: 'flex', gap: '10px' }}>
								<Button fullWidth variant='outlined' size='large' disabled={!hayFiltrosActivos} onClick={handleLimpiarFiltros}>
									Limpiar filtros
								</Button>
								{isAdmin && (
									<Button fullWidth variant='contained' size='large' onClick={() => navigate('/admin/gestion-eventos/nuevo')}>
										Registrar evento.
									</Button>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className={`${styleScss['table-limit']}`} style={{ maxWidth: '100%', overflowWrap: 'break-word', overflowY: 'hidden' }}>
					<Stack padding={2}>
						<TableContainer component={Paper} style={{ overflowY: 'visible', overflowWrap: 'break-word' }}>
							<Table style={{ overflowY: 'visible' }}>
								<TableHead className={`${styleScss['th-custom']}`}>
									<TableRow>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Código evento</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Nombre del evento</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Localidad</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Tipo evento</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>¿Tiene prerregistro?</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha inicio prerregistro</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha fin prerregistro</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha inicio evento</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Fecha final evento</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Estado</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Prerregistrados</h4></TableCell>
										<TableCell style={{ fontFamily: 'Poppins', fontSize: '16.25px', textAlign: 'center' }}><h4>Acciones</h4></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{eventos.map((evento) => (
										<TableRow key={evento.id}>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{evento.codigo_evento}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{evento.nombre_evento}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{getLocalidadLabel(evento)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{getTipoEventoLabel(evento)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{evento.requiere_prerregistro === 'SI' ? 'Sí' : 'No'}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												{evento.requiere_prerregistro === 'SI' ? formatDateDisplay(evento.fecha_inicio_prerregistro) : 'N/A'}
											</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												{evento.requiere_prerregistro === 'SI' ? formatDateDisplay(evento.fecha_fin_prerregistro) : 'N/A'}
											</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formatDateDisplay(evento.fecha_inicio_evento)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{formatDateDisplay(evento.fecha_fin_evento)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>{getEstadoLabel(evento)}</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												{evento.requiere_prerregistro === 'SI' ? <PrerregistradosPorFranja evento={evento} /> : 'N/A'}
											</TableCell>
											<TableCell style={{ fontFamily: 'Poppins', fontSize: '14.5px', textAlign: 'center' }}>
												<MenuAction evento={evento} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
								{!loading && eventos.length === 0 && (
									<tfoot>
										<TableRow>
											<TableCell colSpan={12}>
												<p className='text-center' style={{ width: '100%', textAlign: 'center' }}>
													No se encontraron registros
												</p>
											</TableCell>
										</TableRow>
									</tfoot>
								)}
							</Table>
							<div style={{ fontFamily: 'Poppins', fontSize: '14.5px' }}>
								<TablePagination
									rowsPerPageOptions={[10, 20, 50]}
									component='div'
									style={{ overflow: 'visible', fontFamily: 'Poppins', fontSize: '14.5px' }}
									count={total}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={(event, newPage) => handleChangePage(newPage)}
									onRowsPerPageChange={handleChangeRowsPerPage}
									labelRowsPerPage='Filas por página:'
									labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
									ActionsComponent={({ onPageChange, page: pageNumber, count, rowsPerPage: rpp }) => (
										<div style={{ display: 'flex', alignItems: 'center', fontFamily: 'Poppins', fontSize: '14.5px' }}>
											<IconButton onClick={handleFirstPageButtonClick} disabled={pageNumber === 0} aria-label='first page'>
												<FirstPageIcon />
											</IconButton>
											<IconButton onClick={(event) => onPageChange(event, pageNumber - 1)} disabled={pageNumber === 0} aria-label='previous page'>
												<ChevronLeftIcon />
											</IconButton>
											<IconButton onClick={(event) => onPageChange(event, pageNumber + 1)} disabled={pageNumber >= Math.ceil(count / rpp) - 1} aria-label='next page'>
												<ChevronRightIcon />
											</IconButton>
											<IconButton onClick={handleLastPageButtonClick} disabled={pageNumber >= Math.ceil(count / rpp) - 1} aria-label='last page'>
												<LastPageIcon />
											</IconButton>
										</div>
									)}
								/>
							</div>
						</TableContainer>
					</Stack>
				</div>
			</div>

			{/* Activar evento */}
			<Dialog open={openActivar} onClose={() => setOpenActivar(false)}>
				<DialogTitle>Activar evento</DialogTitle>
				<DialogContent>
					<DialogContentText>
						¿Está seguro de que desea activar el evento "{eventoActivar?.nombre_evento}"? El evento pasará a estado "Activo".
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setOpenActivar(false)}>
						Volver
					</Button>
					<Button variant='contained' color='success' onClick={handleConfirmarActivar} autoFocus>
						Activar evento
					</Button>
				</DialogActions>
			</Dialog>
 
			{/* Finalizar evento */}
			<Dialog open={openFinalizar} onClose={() => setOpenFinalizar(false)}>
				<DialogTitle>Finalizar evento</DialogTitle>
				<DialogContent>
					<DialogContentText>
						¿Está seguro de que desea finalizar el evento "{eventoFinalizar?.nombre_evento}"? El evento pasará a estado "Finalizado".
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setOpenFinalizar(false)}>
						Volver
					</Button>
					<Button variant='contained' color='success' onClick={handleConfirmarFinalizar} autoFocus>
						Finalizar evento
					</Button>
				</DialogActions>
			</Dialog>

			{/* Cancelar evento */}
			<Dialog open={openCancelar} onClose={() => setOpenCancelar(false)}>
				<DialogTitle>Cancelar evento</DialogTitle>
				<DialogContent>
					<DialogContentText>
						¿Está seguro de que desea cancelar el evento "{eventoCancelar?.nombre_evento}"? El evento pasará a estado "Cancelado"; el registro no se eliminará.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setOpenCancelar(false)}>
						Volver
					</Button>
					<Button variant='contained' color='error' onClick={handleConfirmarCancelar} autoFocus>
						Cancelar evento
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reactivar evento */}
			<Dialog open={openReactivar} onClose={() => setOpenReactivar(false)}>
				<DialogTitle>Reactivar evento</DialogTitle>
				<DialogContent>
					<DialogContentText>
						¿Está seguro de que desea reactivar el evento "{eventoReactivar?.nombre_evento}"? El evento pasará de estado "Cancelado" a "Creado".
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setOpenReactivar(false)}>
						Volver
					</Button>
					<Button variant='contained' color='success' onClick={handleConfirmarReactivar} autoFocus>
						Reactivar evento
					</Button>
				</DialogActions>
			</Dialog>

			{/* Publicar evento */}
			<Dialog open={openPublicar} onClose={() => setOpenPublicar(false)}>
				<DialogTitle>Publicar evento</DialogTitle>
				<DialogContent>
					<DialogContentText>
						¿Está seguro de que desea publicar el evento "{eventoPublicar?.nombre_evento}"? El evento pasará a estado "Programado".
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setOpenPublicar(false)}>
						Volver
					</Button>
					<Button variant='contained' color='success' onClick={handleConfirmarPublicar} autoFocus>
						Publicar evento
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} open={open} onClose={() => setOpen(false)}>
				<Alert onClose={() => setOpen(false)} variant='filled' severity={alert ? 'success' : 'error'} sx={{ whiteSpace: 'pre-line' }}>
					{alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
					{alertMsg}
				</Alert>
			</Snackbar>
		</>
	);
};

const AdminEventosEmpleabilidadWrapper = () => {
	return (
		<>
			<PageTitle breadcrumbs={[]}>Gestionar Eventos de Empleabilidad </PageTitle>
			<EventosEmpleabilidadPage />
		</>
	);
};


export { AdminEventosEmpleabilidadWrapper };
